import {
    format,
    formatWithoutTimezone,
    ISO_DATE_ONLY_FORMAT,
    zodDateOfBirthValidation,
    zodFirstNameValidation,
    zodLastNameValidation,
} from '@fruitful/fruitils';
import { Pronouns } from '@fruitful/member-profile';
import { SUPPORT_EMAIL } from '@fruitful/member-profile/src/common/types/Support';
import PronounSelectionSheet from '@fruitful/member-profile/src/personal-info/sheets/PronounSelectionSheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, router } from 'expo-router';
import * as React from 'react';
import { useCallback, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledDateTextInput,
    ControlledInput,
    ControlledInputTrigger,
    ControlledPicker,
    Form,
    isWeb,
    LabelRegular,
    ScreenContainer,
    Spinner,
    tokens,
    USE_FORM_MODE_DEFAULTS,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { useSendEmailToSupport } from '../../common/hooks/useSendEmailToSupport';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import LogRocketFacade from '../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../logger/Logger';
import useUpdateUser from '../../user/hooks/useUpdateUser';
import { useUser } from '../../user/providers/UserProvider';
import {
    SUCCESS_TOAST_DURATION,
    SUCCESS_TOAST_MESSAGE,
    SuccessToastIcon,
} from './SuccessToast';

const personalInfoSchema = z.object({
    firstName: zodFirstNameValidation,
    lastName: zodLastNameValidation,
    pronouns: z.string().optional(),
    dateOfBirth: zodDateOfBirthValidation,
});

type PersonalInfoSchema = z.infer<typeof personalInfoSchema>;

const pronounOptions = Object.values(Pronouns).map((pronoun) => ({
    label: pronoun,
    value: pronoun,
}));

export const PersonalInfo = () => {
    LogRocketFacade.tagPage('Profile/Name');

    const toast = useToastController();
    const sendEmailToSupport = useSendEmailToSupport();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPronounSheetOpen, setIsPronounSheetOpen] = useState(false);

    const authedUser = useAuthedUser();
    const { syncteraKycStatus } = authedUser;

    // Once a user submits their name/date of birth to synctera via KYC, it's no longer editable without contacting HD to do fraud checks
    // However, allow updates if the values are currently null
    const isFirstNameDisabled =
        syncteraKycStatus !== null && authedUser?.firstName != null;
    const isLastNameDisabled =
        syncteraKycStatus !== null && authedUser?.lastName != null;
    const isDateOfBirthDisabled =
        syncteraKycStatus !== null && authedUser?.dateOfBirth != null;
    const isPersonalInfoEditDisabled =
        isFirstNameDisabled || isLastNameDisabled || isDateOfBirthDisabled;

    // Build dynamic message based on which fields are disabled
    const getDisabledFieldsMessage = () => {
        const disabledFields: string[] = [];

        if (isFirstNameDisabled || isLastNameDisabled) {
            disabledFields.push('your name');
        }

        if (isDateOfBirthDisabled) {
            disabledFields.push('your date of birth');
        }

        if (disabledFields.length === 0) {
            return '';
        }

        const fieldsText = disabledFields.join(' or ');
        return `If you need to edit ${fieldsText}, please contact `;
    };

    const { refetch: refetchUser } = useUser();
    const { mutateAsync: updateUser, isPending: isUpdatingUser } =
        useUpdateUser();

    const {
        handleSubmit,
        formState: { isValid, errors },
        register,
        control,
        setValue,
        trigger,
        watch,
    } = useForm<PersonalInfoSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(personalInfoSchema),
        defaultValues: {
            firstName: authedUser?.firstName ?? '',
            lastName: authedUser?.lastName ?? '',
            pronouns: authedUser?.pronouns ?? '',
            dateOfBirth:
                authedUser?.dateOfBirth != null
                    ? formatWithoutTimezone(authedUser.dateOfBirth)
                    : '',
        },
    });

    const pronouns = watch('pronouns');

    const onSubmit: SubmitHandler<PersonalInfoSchema> = useCallback(
        async (data) => {
            setIsSubmitting(true);
            try {
                await updateUser({
                    urlParams: { userId: authedUser.id },
                    body: {
                        firstName: data.firstName,
                        lastName: data.lastName,
                        pronouns: data.pronouns ?? null,
                        dateOfBirth: `${format(data.dateOfBirth, ISO_DATE_ONLY_FORMAT)}T00:00:00Z`,
                    },
                });

                toast.show(SUCCESS_TOAST_MESSAGE, {
                    duration: SUCCESS_TOAST_DURATION,
                    icon: SuccessToastIcon,
                });

                void refetchUser?.();

                router.navigate('/settings');
            } catch (error) {
                captureException(error);
                toast.show('Something went wrong!', { duration: 2000 });
            } finally {
                setIsSubmitting(false);
            }
        },
        [authedUser, updateUser, toast, refetchUser],
    );

    const isFormSubmitting = isSubmitting || isUpdatingUser;

    return (
        <ScreenContainer title="Personal info" hasBackButton={true}>
            {isFormSubmitting ? (
                <Spinner />
            ) : (
                <YStack gap={24} justifyContent="space-between">
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <YStack gap={16}>
                            <XStack gap={12}>
                                <View flex={1}>
                                    <ControlledInput
                                        {...register('firstName')}
                                        label="First name"
                                        control={control}
                                        register={register}
                                        errors={errors}
                                        autoCapitalize="words"
                                        autoComplete="name-given"
                                        textContentType="name"
                                        disabled={isFirstNameDisabled}
                                    />
                                </View>
                                <View flex={1}>
                                    <ControlledInput
                                        {...register('lastName')}
                                        label="Last name"
                                        control={control}
                                        register={register}
                                        errors={errors}
                                        autoCapitalize="words"
                                        autoComplete="name-family"
                                        textContentType="familyName"
                                        disabled={isLastNameDisabled}
                                    />
                                </View>
                            </XStack>

                            {isWeb ? (
                                <ControlledPicker
                                    name="pronouns"
                                    label="Pronouns (optional)"
                                    placeholder="Select pronouns"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    options={pronounOptions}
                                />
                            ) : (
                                <ControlledInputTrigger
                                    name="pronouns"
                                    label="Pronouns (optional)"
                                    placeholder="Select pronouns"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    onTrigger={() => {
                                        setIsPronounSheetOpen(true);
                                    }}
                                />
                            )}

                            <ControlledDateTextInput
                                {...register('dateOfBirth')}
                                label="Date of birth"
                                control={control}
                                register={register}
                                errors={errors}
                                textContentType="none"
                                disabled={isDateOfBirthDisabled}
                            />
                            {isPersonalInfoEditDisabled && (
                                <LabelRegular color="$gray1000">
                                    {getDisabledFieldsMessage()}
                                    <Link
                                        href={`mailto:${SUPPORT_EMAIL}?subject=Personal info change`}
                                        style={{
                                            fontSize: 12, // Hardcoded as a workaround for RNW not setting the right font size.
                                            textDecorationLine: 'underline',
                                            color: tokens.color.light.gray
                                                .gray1000,
                                        }}
                                        onPress={(event) => {
                                            event.preventDefault();
                                            void sendEmailToSupport({
                                                subject: 'Personal info change',
                                            });
                                        }}
                                    >
                                        {SUPPORT_EMAIL}
                                    </Link>
                                </LabelRegular>
                            )}
                        </YStack>
                    </Form>

                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Save"
                        onPress={handleSubmit(onSubmit)}
                        disabled={!isValid || isFormSubmitting}
                    />
                </YStack>
            )}

            {!isWeb && (
                <PronounSelectionSheet
                    isOpen={isPronounSheetOpen}
                    selectedOption={pronouns as Pronouns}
                    onSelectOption={(pronouns: Pronouns | null) => {
                        setValue('pronouns', pronouns ?? '');
                        void trigger('pronouns'); // Manually trigger form validation after setValue
                    }}
                    onClose={() => {
                        setIsPronounSheetOpen(false);
                    }}
                />
            )}
        </ScreenContainer>
    );
};
